import { Link, useLocation, useNavigate, type Location } from "react-router";
import { useContext, useEffect, useState } from "react";
import { CircleHelp, ArrowLeftFromLine, ArrowRightFromLine, Settings, LogOut, ChevronDown } from "lucide-react";
import { LayoutContext } from "../../../context/LayoutContext";
import Tooltip from "../../DesignSystem/Tooltip";
import * as React from "react";
import { UserContext } from "../../../context/UserContext";
import { twMerge } from "tailwind-merge";
import { userDisplayName } from "../../../util/formatting";
import Button from "../../DesignSystem/Button";
import Dropdown from "../../DesignSystem/Dropdown.tsx";
import PurchaserClient from "../../../clients/PurchaserClient.ts";
import { useFeatureAccess, FEATURE_KEYS } from "../../../util/useFeatureAccess";
import { useIsSandboxTeam } from "../../Common/Hooks/useIsSandboxTeam.ts";
import { ICON_KEYS, type HeaderIconKey } from "../Page/Header";

type MenuOption = {
    title: string;
    // The path to link to
    link: string;

    // The optional array of paths that should be considered active for this menu item.
    // If this is not provided, then the link is considered active if the current location starts with the link.
    active?: string[];
    // Section icon, looked up in Header's ICON_KEYS — single source of truth. The sidebar renders it
    // (restyled per active state) and loading fallbacks (PageLoadingState) pass it to the header, so
    // the two can't drift apart.
    iconKey: HeaderIconKey;
    // Optional feature key - if provided, this menu item will be hidden if the feature is disabled
    featureKey?: string;
    // Optional sandbox available - if true, this menu item will be shown in the sandbox environment even if the feature is disabled
    sandboxAvailable?: boolean;
};

// Duration of the sidebar collapse/expand slide. Shared with SidebarMenuPage's width transition
// and the post-animation timers so they stay in sync if the speed changes.
export const MENU_ANIMATION_MS = 200;

// Nav labels / team name (single line) start fading in partway through the slide so there isn't a
// noticeable gap where they're missing. The support text waits the full slide (it wraps).
const MENU_TEXT_FADE_DELAY_MS = 80;

export const baseOptions: MenuOption[] = [
    {
        title: "Home",
        link: "/",
        iconKey: "Home",
    },
    {
        title: "Dashboards",
        link: "/dashboards",
        iconKey: "Dashboard",
    },
    {
        title: "Requisitions",
        link: "/requisitions",
        iconKey: "Requisition",
        featureKey: FEATURE_KEYS.REQUISITIONS,
    },
    {
        title: "RFQs",
        link: "/rfqs",
        iconKey: "RFQ",
    },
    {
        title: "Purchase Orders",
        link: "/purchase-orders",
        iconKey: "PurchaseOrder",
        featureKey: FEATURE_KEYS.PO_MANAGEMENT,
    },
    // TODO: Uncomment once we add this to the product
    // {
    //     title: "Sourcing Requests",
    //     link: "/sourcing-requests",
    //     iconKey: "SourcingRequest",
    // },
    // {
    //     title: "Catalog",
    //     link: "/catalog",
    //     iconKey: "IndustrialProducts",
    // },
    {
        title: "Vendor Management",
        link: "/contacts",
        active: ["/contacts", "/vendors"],
        iconKey: "ContactsAndVendors",
    },
    // TODOMD: Make visible to users when SpendAnalysis has been live for awhile
    // {
    //     title: "Spend Analysis",
    //     link: "/spend-analysis",
    //     iconKey: "SpendAnalysis",
    // },
    {
        title: "Vendor Discovery",
        link: "/vendor-discovery",
        iconKey: "VendorDiscovery",
        featureKey: FEATURE_KEYS.VENDOR_DISCOVERY,
    },
    {
        title: "Workspace Settings",
        link: "/settings",
        iconKey: "Settings",
    },
];

/**
 * Determines if a menu item is actively selected or not.  This is determined by looking to see if the current
 * location starts with the path specified in the menu item.  Special handling is needed for home since it is just "/".
 * @param option
 */
function isActive(location: Location, option: MenuOption): boolean {
    if (location.pathname === "/" && option.link === "/") return true;

    // If the option has an active array, then we need to check if the current location starts with any of the paths in the array.
    if (option.active) {
        return option.active.some(path => location.pathname.toLowerCase().startsWith(path));
    }

    return location.pathname.toLowerCase() === option.link && option.link !== "/";
}

const MenuOptionView = (props: { option: MenuOption; isMenuExpanded: boolean; showLabel: boolean; isActive: boolean }) => {
    const { option, isMenuExpanded, showLabel, isActive } = props;

    const icon = React.cloneElement(ICON_KEYS[option.iconKey] as React.ReactElement, {
        // @ts-ignore
        className: `my-1 h-4 w-4 shrink-0 stroke-stone-400 ${isActive ? "stroke-stone-950" : "stroke-stone-400"}`,
    });

    return (
        <>
            <Tooltip active={!isMenuExpanded} key={option.title} text={option.title} horizontalOffset={6} align="right">
                <div key={option.title}>
                    <Link
                        to={option.link}
                        className={`mb-1 flex w-full cursor-pointer items-center gap-3 rounded py-1 pl-[9px] font-medium text-stone-600 hover:bg-stone-200 hover:text-stone-700 ${isActive && "bg-stone-200 text-stone-950"}`}
                    >
                        {icon}
                        {/* Inline opacity transition (Tailwind transition-opacity doesn't animate
                            here). Kept in the DOM so its width holds while collapsed. */}
                        <span
                            className="navMenuItem text-nowrap"
                            style={{ opacity: showLabel ? 1 : 0, transition: "opacity 150ms ease-in-out" }}
                        >
                            {option.title}
                        </span>
                    </Link>
                </div>
            </Tooltip>
        </>
    );
};

export default function DesktopMenu({ className = "", showCollapseOption = true, showFullMenu = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const isSandbox = useIsSandboxTeam();

    const { user, logout } = useContext(UserContext);
    const { isMenuExpanded: isLayoutMenuExpanded, toggleMenu } = useContext(LayoutContext);
    const [isImpersonating, setIsImpersonating] = useState("");
    const [showNewCollapse, setShowNewCollapse] = useState(false);
    const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);

    const { isFeatureEnabled } = useFeatureAccess();

    let isMenuExpanded = isLayoutMenuExpanded;

    if (showFullMenu) {
        isMenuExpanded = true;
    }

    // Gates the expanded text (nav labels, team name, support text): fades in shortly after the
    // open begins and out immediately on collapse. Starts matching the current state so there's no
    // delay on initial load, where there's no animation.
    const [showExpandedText, setShowExpandedText] = useState(isMenuExpanded);

    useEffect(() => {
        if (!isMenuExpanded) {
            setShowExpandedText(false);
            return;
        }
        const timer = setTimeout(() => setShowExpandedText(true), MENU_TEXT_FADE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [isMenuExpanded]);

    // GSNOTE: Odd hack, but in order for the animations to be pretty, we need to shove the menu items over to the right when the menu is collapsed so that the user can't click on them and so that the tooltip doesn't flash when the user hovers over the menu items
    useEffect(() => {
        if (!isMenuExpanded) {
            // Wait for the animation to finish and then apply the ml-14 class to each of the menu items to prevent the user from clicking on them
            setTimeout(() => {
                setShowNewCollapse(true);
                const menuItems = document.querySelectorAll(".navMenuItem");
                menuItems.forEach(item => {
                    item.classList.add("ml-14");
                });
            }, MENU_ANIMATION_MS);
        }
        if (isMenuExpanded) {
            setShowNewCollapse(false);
            const menuItems = document.querySelectorAll(".navMenuItem");
            menuItems.forEach(item => {
                item.classList.remove("ml-14");
            });
        }
    }, [isMenuExpanded]);

    // detect impersonation
    useEffect(() => {
        try {
            const token = localStorage.getItem("token");
            if (token) {
                const decodedJwt = JSON.parse(atob(token.split(".")[1]).toString());
                if (decodedJwt["isImpersonating"]) setIsImpersonating(decodedJwt["isImpersonating"]);
            }
        } catch (err) {
            console.error("Error parsing token:", err);
        }
    }, []);

    const options: MenuOption[] = baseOptions.filter(opt => !opt.featureKey || isFeatureEnabled(opt.featureKey));

    // Filter out the sandbox unavailable options if we're in the sandbox
    const filteredOptions = options.filter(opt => {
        if (isSandbox) {
            return opt.sandboxAvailable !== false; // Show the option if sandboxAvailable is true or undefined, hide it if it's explicitly set to false
        }
        return true; // If not in sandbox, show all options that passed the feature flag check
    });

    const getTruncatedTeamName = (name: string) => {
        if (name.length > 16) {
            return name.substring(0, 16) + "...";
        }
        return name;
    };

    return (
        <aside
            className={`hidden min-h-screen w-full shrink-0 overflow-x-hidden bg-stone-100 px-3 md:block ${className}`}
        >
            {/* Scrollable so a menu taller than the viewport (mobile) can reach its bottom items.
                The support text below stays single-line until expanded, so this doesn't flash a
                scrollbar while the desktop panel is mid-slide. */}
            <div className="flex h-screen flex-col justify-between overflow-x-hidden overflow-y-auto">
                <div>
                    <div className="group mb-2 h-12 w-full py-3 text-left focus:outline-none">
                        <div className="flex h-[32px] items-center gap-1.5 rounded py-2 pl-1">
                            {!user ? (
                                <div className="h-6 w-6 min-w-6 animate-pulse rounded bg-stone-200" />
                            ) : user.lastTeam.logoUrl ? (
                                <img src={user.lastTeam.logoUrl} alt={user.lastTeam.name} className="h-5 max-w-12" />
                            ) : (
                                <div className="flex h-6 w-6 min-w-6 items-center justify-center rounded bg-stone-200 px-1 font-bold text-stone-600">
                                    {user.lastTeam.name?.toUpperCase().substring(0, 1)}
                                </div>
                            )}
                            <div
                                className="flex grow items-center text-nowrap"
                                style={{ opacity: showExpandedText ? 1 : 0, transition: "opacity 150ms ease-in-out" }}
                            >
                                {!user ? (
                                    <div className="h-4 w-28 animate-pulse rounded bg-stone-200" />
                                ) : (
                                    <div
                                        className="line-clamp-1 grow font-medium text-stone-600"
                                        aria-label={isImpersonating || user.lastTeam.name || ""}
                                    >
                                        {getTruncatedTeamName(isImpersonating || user.lastTeam.name || "")}
                                    </div>
                                )}
                            </div>
                            {user && user.userTeams && user.userTeams.length > 1 && (
                                <Tooltip text="Switch Team" verticalOffset={4} active={!isTeamDropdownOpen}>
                                    <Dropdown
                                        usePortal
                                        portalAnchor="bottom start"
                                        buttonOverride={
                                            <Button
                                                color="clear"
                                                className="w-6 justify-center hover:bg-white"
                                                icon={<ChevronDown className="stroke-stone-600" />}
                                            />
                                        }
                                        itemWrapperClassName="z-50 w-[230px] max-h-[312px] p-3 overflow-y-auto thin-scrollbar"
                                        onOpen={() => setIsTeamDropdownOpen(true)}
                                        onClose={() => setIsTeamDropdownOpen(false)}
                                    >
                                        {user.userTeams.map((team, idx) => (
                                            <Button
                                                color="clear"
                                                onClick={() => PurchaserClient.switchTeam(team.teamId).then(() => window.location.reload())}
                                                className={twMerge("mb-2 w-full px-2 py-1 font-medium", idx === user.userTeams.length - 1 && "mb-0")}
                                                key={team.teamId}
                                                text={team.team.name}
                                            />
                                        ))}
                                    </Dropdown>
                                </Tooltip>
                            )}
                        </div>
                        {isImpersonating ? (
                            <Tooltip text="Log out to stop impersonation">
                                <div className="bg-red-700 p-2 text-center font-bold text-white">IMPERSONATING</div>
                            </Tooltip>
                        ) : null}
                    </div>
                    {filteredOptions.map(option => {
                        return (
                            <MenuOptionView
                                key={option.title}
                                option={option}
                                isMenuExpanded={isMenuExpanded}
                                showLabel={showExpandedText}
                                isActive={isActive(location, option)}
                            />
                        );
                    })}
                </div>
                <div>
                    {/* Cross-fade the help text (expanded) and the ? icon (collapsed) in one grid
                        cell, timed with the nav labels via showExpandedText. The text is fixed to its
                        expanded width so the narrowing panel clips it (like the nav labels) instead of
                        rewrapping it as the width animates. */}
                    <div className={`hidden grid-cols-1 md:grid ${showCollapseOption ? "mb-6" : "mb-2.5"}`}>
                        <div
                            className={`col-start-1 row-start-1 w-[200px] px-4 py-2.5 text-xs text-wrap ${showExpandedText ? "" : "pointer-events-none"}`}
                            style={{ opacity: showExpandedText ? 1 : 0, transition: "opacity 150ms ease-in-out" }}
                        >
                            Email{" "}
                            <a className="font-semibold" href="mailto:support@purchaser.ai">
                                support@purchaser.ai
                            </a>{" "}
                            with questions and feedback.
                        </div>
                        <div
                            className={`col-start-1 row-start-1 flex items-start justify-center ${showExpandedText ? "pointer-events-none" : ""}`}
                            style={{ opacity: showExpandedText ? 0 : 1, transition: "opacity 150ms ease-in-out" }}
                        >
                            <Tooltip active={!isMenuExpanded} text="Email support@purchaser.ai" align="right" horizontalOffset={6}>
                                <a
                                    className="flex justify-center rounded px-2 py-2 hover:bg-stone-200 hover:text-stone-700"
                                    href="mailto:support@purchaser.ai"
                                >
                                    <CircleHelp className="h-4 stroke-stone-400" />
                                </a>
                            </Tooltip>
                        </div>
                    </div>

                    <div className="pb-3 md:hidden">
                        <div className="mb-2 flex border-t border-stone-200 pt-2.5">
                            <div className="mx-1.5 mt-0.5">
                                <div
                                    className={twMerge(
                                        user?.iconBackgroundColorClass ? user.iconBackgroundColorClass : "bg-emerald-600",
                                        "flex h-5 w-5 items-center justify-center rounded text-xs text-white",
                                    )}
                                >
                                    {user?.first_name.charAt(0).toUpperCase()}
                                </div>
                            </div>
                            <div>
                                <div>{userDisplayName(user)}</div>
                                <div className="text-xs text-stone-600">{user?.email}</div>
                            </div>
                        </div>
                        <Button
                            color="clear"
                            text="User Settings"
                            icon={<Settings className="h-4 w-4 stroke-stone-400" />}
                            className="mb-2 w-full"
                            href="/settings/user/profile"
                        />
                        <Button
                            color="clear"
                            text="Sign Out"
                            icon={<LogOut className="h-4 w-4 stroke-stone-400" />}
                            className="w-full"
                            onClick={async () => {
                                await logout();
                                navigate("/login");
                            }}
                        />
                    </div>
                    {showCollapseOption && !showNewCollapse && (
                        <button
                            type="button"
                            className={`mb-2 flex w-full cursor-pointer items-center justify-end py-2 text-stone-500`}
                            onClick={toggleMenu}
                        >
                            <span className={`${isMenuExpanded ? "visible" : "hidden"} mr-2 text-nowrap transition-all`}>Collapse</span>
                            <ArrowLeftFromLine className="h-4 w-4 shrink-0 stroke-stone-400" />
                        </button>
                    )}
                    {showCollapseOption && showNewCollapse && (
                        <Tooltip text="Expand" horizontalOffset={6} align="right">
                            <button
                                type="button"
                                className="mb-2 flex w-full cursor-pointer items-center justify-center rounded p-2 text-stone-500 hover:bg-stone-200 hover:text-stone-700"
                                onClick={toggleMenu}
                            >
                                <ArrowRightFromLine className="h-4 w-4 shrink-0 stroke-stone-400" />
                            </button>
                        </Tooltip>
                    )}
                </div>
            </div>
        </aside>
    );
}
